export type FilterValue = string | number | boolean;

export interface FilterItem {
  value: FilterValue | FilterValue[];
  label?: string | string[];
}

export interface FilterState<
  TFilterKeys extends string = string,
  TMeta extends Record<string, unknown> = Record<string, unknown>
> {
  filter: Partial<Record<TFilterKeys, FilterItem>>;
  meta: TMeta;
}

export type UpdateFilterActionCreator = (
  name: string,
  value: FilterValue | FilterValue[],
  label?: string | string[]
) => {
  type: 'UpdateFilter';
  payload: {
    name: string;
    value: FilterValue | FilterValue[];
    label?: string | string[];
  };
};

export type UpdateFilterLabelsActionCreator = (
  name: string,
  label?: string | string[]
) => {
  type: 'UpdateFilterLabels';
  payload: { name: string; label?: string | string[] };
};

export type ClearAllActioncreator = (filterKeys: string[]) => {
  type: 'ClearAll';
  payload: { filterKeys: string[] };
};

export const updateFilterAction: UpdateFilterActionCreator = (
  name,
  value,
  label
) => ({
  type: 'UpdateFilter',
  payload: { name, value, label },
});

export const UpdateFilterLabels: UpdateFilterLabelsActionCreator = (
  name,
  label
) => ({
  type: 'UpdateFilterLabels',
  payload: { name, label },
});

export const updateFilterLabelAction: UpdateFilterLabelsActionCreator = (
  name,
  label
) => ({
  type: 'UpdateFilterLabels',
  payload: { name, label },
});

export const clearAllAction: ClearAllActioncreator = (
  filterKeys: string[]
) => ({
  type: 'ClearAll',
  payload: { filterKeys },
});

export type FilterAction =
  | ReturnType<UpdateFilterActionCreator>
  | ReturnType<UpdateFilterLabelsActionCreator>
  | ReturnType<ClearAllActioncreator>;

export type ExtraFilterReducer = (
  newState: FilterState,
  previousState?: FilterState,
  action?: FilterAction
) => FilterState;

export const getFilterReducer =
  (extraReducer: ExtraFilterReducer = (newState) => newState) =>
  (state: FilterState, action: FilterAction) => {
    let newState: FilterState = state;
    const previousState: FilterState = state;
    switch (action.type) {
      case 'UpdateFilter': {
        const { name, value, label } = action.payload;

        newState = {
          ...state,
          filter: { ...state.filter, [name]: { value, label } },
        };

        if (
          value === undefined ||
          (Array.isArray(value) && value.length === 0)
        ) {
          delete newState.filter[name];
        }

        break;
      }

      case 'UpdateFilterLabels': {
        const { name, label } = action.payload;

        newState = state;
        if (state.filter[name]?.value !== undefined) {
          newState = {
            ...state,
            filter: {
              ...state.filter,
              [name]: { ...state.filter[name], label },
            },
          };
        }

        break;
      }

      case 'ClearAll': {
        const { filterKeys } = action.payload;
        newState = { ...state };
        filterKeys.forEach(function (key) {
          delete newState.filter[key];
        });

        break;
      }
    }

    return extraReducer(newState, previousState, action);
  };
